import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Broadcast.css';
import {
  createTeamsPayload,
  createTeamsPayloadWithAdaptiveCard,
  extractPlainText,
  convertQuillToAdaptiveCardText
} from './teamsFormatter.js';
import { useNavigate } from 'react-router-dom';



function parseAttachment(dataUrl, index) {
  const [meta, base64] = dataUrl.split(',');
  const mimeType = meta.match(/:(.*?);/)[1];
  const ext = mimeType.split('/')[1];
  return { filename: `attachment_${index + 1}.${ext}`, base64, mimeType };
}

function compressImage(file, maxWidth = 800, maxHeight = 600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > maxWidth || height > maxHeight) {
        const ratio = width / height;
        if (width > height) { width = maxWidth; height = maxWidth / ratio; }
        else { height = maxHeight; width = maxHeight * ratio; }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = reject;
  });
}

function isQuillEmpty(html) {
  if (!html || html.trim() === '') return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
}

const DEFAULT_TEMPLATE = [
  { title: 'Details', content: '' },
  { title: 'Impact', content: '' },
  { title: 'Action required', content: '' },
  { title: 'Distribution', content: '' },
  { title: 'Schedule', content: '' },
  { title: 'On FastTrack', content: '' },
];

export default function Broadcast() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [type, setType] = useState('msg');
  const [sendTo, setSendTo] = useState('');
  const [savedContent, setSavedContent] = useState([]);
  const [error, setError] = useState({ text: false, message: '' });
  const [validatedEmails, setValidatedEmails] = useState({ validEmails: [], invalidEmails: [] });
  const [selectedAudience, setSelectedAudience] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberCount, setMemberCount] = useState(null);
  const [dlEmails, setDlEmails] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentKey, setAttachmentKey] = useState(Date.now());
  const [isDragOver, setIsDragOver] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [selectedQuickTime, setSelectedQuickTime] = useState(null);
  const [scheduleInputsVisible, setScheduleInputsVisible] = useState(true);
  const [showSavedDrafts, setShowSavedDrafts] = useState(false);
  const [draftTemplates, setDraftTemplates] = useState([]);
  const [isFetchingDrafts, setIsFetchingDrafts] = useState(false);
  const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [pendingDraftPayload, setPendingDraftPayload] = useState(null);
  const [showScheduledMsgs, setShowScheduledMsgs] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [isFetchingScheduled, setIsFetchingScheduled] = useState(false);
  const [templateContent, setTemplateContent] = useState([...DEFAULT_TEMPLATE]);
  const [isScheduleSubmit, setIsScheduleSubmit] = useState(false);
  const [pendingSchedulePayload, setPendingSchedulePayload] = useState(null);
  const [sendToAllUsers, setSendToAllUsers] = useState(false);
  const allUsersRef = useRef(false);   // add useRef to the React import
  const [isSending, setIsSending] = useState(false);
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  const [statusReceived, setStatusReceived] = useState(false);
  const [IsSent, setIsSent] = useState(false);


  function handleAllUsersToggle(checked) {
    setSendToAllUsers(checked);
    allUsersRef.current = checked;
    if (checked) {
      setSendTo('');
      setGroupSearch('');
      setGroupResults([]);
      setSelectedGroup(null);
      setMemberCount(null);
      setDlEmails('');
      setValidatedEmails({ validEmails: [], invalidEmails: [] });
    }
  }


  const getNext5Days = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + i);
      return {
        label: i === 0 ? 'Today' : days[d.getDay()],
        num: d.getDate(),
        isoDate: d.toISOString().split('T')[0],
        display: `${months[d.getMonth()]} ${d.getDate()}`
      };
    });
  };

  const QUICK_TIMES = ['9:00 AM', '12:00 PM', '3:00 PM', '6:00 PM'];

  const to24h = (t) => {
    const [time, ampm] = t.split(' ');
    let [h, m] = time.split(':').map(Number);
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const toDisplay = (time24) => {
    if (!time24) return null;
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  useEffect(() => {
    const tooltips = [
      { selector: ".ql-header[value='1']", title: "Heading 1" },
      { selector: ".ql-header[value='2']", title: "Heading 2" },
      { selector: ".ql-header:not([value])", title: "Normal Text" },
      { selector: ".ql-bold", title: "Bold (Ctrl + B)" },
      { selector: ".ql-italic", title: "Italic (Ctrl + I)" },
      { selector: ".ql-underline", title: "Underline (Ctrl + U)" },
      { selector: ".ql-strike", title: "Strikethrough" },
      { selector: ".ql-color", title: "Text Color" },
      { selector: ".ql-background", title: "Highlight Color" },
      { selector: ".ql-list[value='ordered']", title: "Numbered List" },
      { selector: ".ql-list[value='bullet']", title: "Bullet List" },
      { selector: ".ql-indent[value='-1']", title: "Decrease Indent" },
      { selector: ".ql-indent[value='+1']", title: "Increase Indent" },
      { selector: ".ql-blockquote", title: "Blockquote" },
      { selector: ".ql-link", title: "Insert/Edit Link" }
    ];
    tooltips.forEach(({ selector, title }) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('title', title);
    });
  }, []);

  useEffect(() => {
    if (groupSearch.trim().length < 2) { setGroupResults([]); return; }
    const timer = setTimeout(async () => {
      setGroupLoading(true);
      try {
        const res = await fetch(`/api/ad/groups?search=${encodeURIComponent(groupSearch)}`);
        const data = await res.json();
        setGroupResults(data.groups || []);
      } catch (e) { console.error('Group search error:', e); }
      finally { setGroupLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [groupSearch]);

  useEffect(() => {
    async function loadInitialGroups() {
      setGroupLoading(true);
      try {
        const res = await fetch(`/api/ad/groups/initial`);
        const data = await res.json();
        setGroupResults(data.groups || []);
      } catch (e) { console.error('Initial load error:', e); }
      finally { setGroupLoading(false); }
    }
    loadInitialGroups();
  }, []);

  useEffect(() => {
    if (!isSending) return;

    const timer = setTimeout(() => {
      if (!statusReceived) {
        setIsSending(false);
        setShowProcessingPopup(true);
      }
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearTimeout(timer);
  }, [isSending, statusReceived]);


  async function handleGroupSelect(group) {
    setSelectedGroup(group);
    setGroupSearch(group.name);
    setGroupResults([]);
    setMembersLoading(true);
    setMemberCount(null);
    setDlEmails('');
    try {
      const res = await fetch(`/api/ad/groups/${group.id}/members`);
      const data = await res.json();
      if (allUsersRef.current) return;   // toggle was turned on mid-fetch
      setDlEmails(data.emails.join(';'));
      setMemberCount(data.count);
    } catch (e) {
      console.error('Member fetch error:', e);
      setError({ text: true, message: 'Failed to fetch group members' });
    } finally { setMembersLoading(false); }
  }

  const fetchScheduledNotifications = useCallback(async () => {
    setIsFetchingScheduled(true);
    try {
      const res = await fetch('/api/scheduled-notifications', { credentials: 'include' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setScheduledNotifications(await res.json());
    } catch (e) { console.error('Scheduled fetch error:', e); }
    finally { setIsFetchingScheduled(false); }
  }, []);

  const deleteScheduledNotification = useCallback(async (id) => {
    if (!window.confirm('Cancel this scheduled message?')) return;
    try {
      const res = await fetch(`/api/scheduled-notifications/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchScheduledNotifications();
      else { const err = await res.json(); alert(err.error || 'Failed to cancel'); }
    } catch (e) { console.error('Delete scheduled error:', e); }
  }, [fetchScheduledNotifications]);

  const handleAttachmentChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const compressed = await compressImage(file);
        setAttachments([compressed]);
        setAttachmentKey(Date.now());
      } catch (err) { console.error('Compression error:', err); }
    }
  }, []);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const compressed = await compressImage(file);
        setAttachments([compressed]);
        setAttachmentKey(Date.now());
      } catch (err) { console.error('Drop error:', err); }
    }
  }, []);

  const handleDeleteAttachment = useCallback(() => {
    setAttachments([]);
    setAttachmentKey(Date.now());
  }, []);

  const fetchDraftTemplates = useCallback(async () => {
    setIsFetchingDrafts(true);
    try {
      const res = await fetch('/api/draft-templates', { credentials: 'include' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setDraftTemplates(await res.json());
    } catch (e) { console.error('Draft fetch error:', e); }
    finally { setIsFetchingDrafts(false); }
  }, []);

  const loadDraftTemplate = useCallback((draft) => {
    const payload = draft.payload;
    if (!payload) return;

    setType(payload.type || 'msg');
    setText(payload.rawText || '');
    // sendTo and dlEmails intentionally NOT restored from drafts
    setAttachments(payload.rawAttachments || []);
    setAttachmentKey(Date.now());

    setTemplateContent(
      payload.rawTemplateContent && payload.rawTemplateContent.length > 0
        ? payload.rawTemplateContent.map(item => ({
          title: item.title,
          content: item.content || ''
        }))
        : [...DEFAULT_TEMPLATE]
    );

    setSendToAllUsers(false);                  // ◄ ADD
    allUsersRef.current = false;               // ◄ ADD

    setSelectedGroup(null);
    setGroupSearch('');
    setMemberCount(null);
    setIsScheduled(false);
    setScheduleDate('');
    setScheduleTime('');
    setSelectedQuickTime(null);
    setShowSavedDrafts(false);

  }, []);

  const deleteDraftTemplate = useCallback(async (draftId) => {
    if (!window.confirm('Delete this saved draft?')) return;
    try {
      const res = await fetch(`/api/draft-templates/${draftId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) fetchDraftTemplates();
    } catch (e) { console.error('Delete error:', e); }
  }, [fetchDraftTemplates]);

  const confirmSaveDraft = useCallback(async () => {
    if (!templateNameInput.trim()) return;
    setIsSavingDraft(true);

    try {
      if (isScheduleSubmit) {
        // send the scheduled notification with the name attached
        const res = await fetch('/api/notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...pendingSchedulePayload,
            notificationName: templateNameInput.trim(),
          }),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to schedule');
        setSavedContent([pendingSchedulePayload]);
        clearMessage();
        fetchScheduledNotifications();   // NEW — refresh the list so it shows immediately
      } else {
        // save draft — unchanged
        const res = await fetch('/api/draft-templates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ templateName: templateNameInput.trim(), payload: pendingDraftPayload })
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed');
        if (showSavedDrafts) fetchDraftTemplates();
      }

      setShowTemplateNameModal(false);
      setPendingDraftPayload(null);
      setPendingSchedulePayload(null);
      setIsScheduleSubmit(false);
      setTemplateNameInput('');
    } catch (e) {
      console.error('confirmSaveDraft error:', e);
    } finally {
      setIsSavingDraft(false);
    }
  }, [templateNameInput, pendingDraftPayload, pendingSchedulePayload, isScheduleSubmit, showSavedDrafts, fetchDraftTemplates, fetchScheduledNotifications]);
  async function validateEmails() {
    const emailList = sendTo.split(';').map(email => email.trim());
    try {
      const response = await fetch('/api/validate-emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: emailList })
      });
      if (!response.ok) throw new Error('Failed to validate emails');
      const data = await response.json();
      setValidatedEmails(data);
    } catch (error) {
      console.error('Error validating emails:', error);
      setValidatedEmails({ validEmails: [], invalidEmails: [] });
      setError({ text: true, message: 'Error validating emails' });
    }
  }

  function handleTypeChange(event) {
    const newType = event.target.value;
    setType(newType);
    setTemplateContent([...DEFAULT_TEMPLATE]);
    if (newType !== 'table') setText('');
  }

  function handleTemplateChange(index, content) {
    const updated = [...templateContent];
    updated[index].content = content;
    setTemplateContent(updated);
  }

  function clearMessage() {
    setText(''); setType('msg'); setSendTo('');
    setTemplateContent([...DEFAULT_TEMPLATE]); setSavedContent([]);
    setError({ text: false, message: '' });
    setValidatedEmails({ validEmails: [], invalidEmails: [] });
    setDlEmails(''); setSelectedGroup(null); setGroupSearch(''); setMemberCount(null);
    setAttachments([]); setAttachmentKey(Date.now());
    setIsScheduled(false); setScheduleDate(''); setScheduleTime('');
    setSelectedQuickTime(null); setScheduleInputsVisible(true);
    setSendToAllUsers(false); allUsersRef.current = false;
  }

  async function validateFields() {
    if (type === 'msg' && isQuillEmpty(text)) {
      setError({ text: true, message: 'Text content is required!' });
      return false;
    }
    setError({ text: false, message: '' });
    return true;
  }

  async function saveMessage() {
    const check = await validateFields();
    if (!check) return;
    const draftPayload = {
      type,
      rawText: text,
      // rawSendTo intentionally omitted — don't persist recipients in drafts
      rawAttachments: attachments,
      // rawDlEmails intentionally omitted — don't persist DL emails in drafts
      rawTemplateContent: templateContent,
      timestamp: new Date().toISOString(),
    };
    setPendingDraftPayload(draftPayload);
    setTemplateNameInput('');
    setShowTemplateNameModal(true);
  }

  const splitEmails = (str) => (str || '').split(';').map(e => e.trim()).filter(Boolean);

  function buildPayload(scheduledISO) {
    const manualRecipients = sendToAllUsers ? [] : splitEmails(sendTo);
    const dlList = sendToAllUsers ? [] : splitEmails(dlEmails);
    const allRecipients = [...manualRecipients, ...dlList];

    const common = {
      type,
      dlRecipients: dlList,
      dlName: sendToAllUsers ? null : (selectedGroup?.name || null),
      sendToAllUsers,
      scheduledDateTime: scheduledISO,
      timestamp: new Date().toISOString(),
    };

    if (type === 'msg') {
      return {
        ...createTeamsPayload('', text, allRecipients.join(';'), attachments, [], {}),
        ...common,
      };
    }

    const filteredContent = templateContent
      .filter(item => item.content.replace(/<p><br><\/p>/g, '').replace(/<[^>]*>/g, '').trim() !== '')
      .map(item => ({ title: item.title, content: convertQuillToAdaptiveCardText(item.content) }));

    return {
      ...common,
      sendTo: allRecipients,
      attachments,
      content: filteredContent,
      plainText: filteredContent.map(i => `${i.title}: ${i.content}`).join('\n'),
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const check = await validateFields();
    if (!check) return;

    if (sendToAllUsers && !window.confirm('This will send to ALL users. Continue?')) return;

    if (isScheduled) {
      if (!scheduleDate || !scheduleTime) {
        setError({ text: true, message: 'Please select both date and time for scheduling!' });
        return;
      }
      const scheduledDate = new Date(`${scheduleDate}T${scheduleTime}:00`);
      if (scheduledDate <= new Date()) {
        setError({ text: true, message: 'Scheduled time must be in the future!' });
        return;
      }
      if (!sendToAllUsers) await validateEmails();

      setPendingSchedulePayload(buildPayload(scheduledDate.toISOString()));
      setIsScheduleSubmit(true);
      setTemplateNameInput('');
      setShowTemplateNameModal(true);
      return;
    }

    if (!sendToAllUsers) await validateEmails();
    const teamsPayload = buildPayload(null);

    try {
      setIsSending(true);
      setStatusReceived(false);
      setShowProcessingPopup(false);

      const response = await fetch('/api/notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teamsPayload),
      });

      // The API responded, so stop the loading state immediately.
      setStatusReceived(true);
      setIsSending(false);

      if (!response.ok) {
        let errorMessage = 'Failed to send message';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Response wasn't JSON; keep the default error.
        }

        throw new Error(errorMessage);
      }

      // Success
      setSavedContent([teamsPayload]);
      clearMessage();

    } catch (error) {
      console.error('Error sending message:', error);

      // Important:
      // The request has already responded, so don't show
      // the 2-minute "still processing" popup.
      setIsSending(false);
      setStatusReceived(true);
      setShowProcessingPopup(false);

      setError({
        text: true,
        message: error.message || 'Failed to send message',
      });
    }
    finally {
      setIsSent(true)
      setIsSending(false);
      setStatusReceived(true);
      setShowProcessingPopup(false);
    }

  }

  const messageModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['link'],
        //  ['blockquote', 'link'],
      ],
      handlers: { 'custom-upload': handleFileUpload },
    },
  };

  const tableModules = {
    toolbar: {
      container: [
        ['bold', 'italic'],
        [{ 'list': 'bullet' }, { indent: '-1' }, { indent: '+1' }, 'link'],
      ],
    },
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent', 'color', 'background', 'link',
  ];

  return (
    // ── bc-root scopes ALL styles so nothing leaks into other pages ──
    <div className="bc-root">
      <h1 className="bc-page-title">Broadcast Dashboard</h1>

      <div className="bc-container">
        {isSending && (
          <div className="loading-container">
            <div className="loading-card">
              <div className="spinner" />
              <p>Sending notification...</p>
              <span>Please do not retry.</span>
            </div>
          </div>
        )}

        {showProcessingPopup && (
          <div className="popup-overlay">
            <div className="popup">
              <h3>Notification is being Processed</h3>

              <p>
                Sending Notifications takes a long time depending upon the size of the Audience.
                Please do not Retry.
              </p>

              <p>
                You can check the report after <strong>5–6 hours</strong>{' '}
                under the <strong>Broadcast Report</strong> tab.
              </p>


              <button onClick={() => setShowProcessingPopup(false)}>
                OK
              </button>
            </div>
          </div>
        )}

        {IsSent && (
          <div className="popup-overlay">
            <div className="popup">
              <h3>Notification is Sent successfully</h3>
              <p>
                You can check the report after under the <strong>Broadcast Report</strong> tab.
              </p>
              <button onClick={()=>{
                   navigate('/BroadcastReport');
              }}>OK
              </button>
            </div>
          </div>
        )}

        <form className="bc-section-container" onSubmit={handleSubmit}>

          {/* ── LEFT COLUMN ── */}
          <div className="bc-main-col">

            {/* Send To */}
            <label htmlFor="bc-sendTo">Send To</label>
            <input
              type="text" id="bc-sendTo" value={sendTo}
              onChange={e => setSendTo(e.target.value)}
              placeholder={sendToAllUsers ? 'Sending to all users' : 'Enter email addresses separated by semicolon'}
              disabled={sendToAllUsers}
            />

            {/* AD Group Search */}
            <div className={`bc-audience-selector${sendToAllUsers ? ' bc-disabled-field' : ''}`}>
              <label htmlFor="bc-groupSearch">Select AD Group (Distribution List)</label>
              <div className="bc-group-search-wrapper">

                <input
                  type="text" id="bc-groupSearch" value={groupSearch}
                  onChange={e => { setGroupSearch(e.target.value); setSelectedGroup(null); setMemberCount(null); setDlEmails(''); }}
                  placeholder={sendToAllUsers ? 'Sending to all users' : 'Type to search AD groups...'}
                  autoComplete="off"
                  disabled={sendToAllUsers}
                />

                {!sendToAllUsers && groupLoading && <span className="bc-group-spinner">Searching...</span>}
                {!sendToAllUsers && membersLoading && <span className="bc-group-spinner">Fetching members...</span>}
                {!sendToAllUsers && memberCount !== null && !membersLoading && (
                  <span className="bc-member-count">✓ {memberCount} members loaded</span>
                )}
                {!sendToAllUsers && groupSearch.trim().length > 0 && !selectedGroup && !groupLoading && groupResults.length === 0 && (
                  <ul className="bc-group-dropdown">
                    <li className="bc-group-dropdown-item no-results">No results available</li>
                  </ul>
                )}
                {!sendToAllUsers && groupResults.length > 0 && (
                  <ul className="bc-group-dropdown">
                    <li className="bc-group-dropdown-item no-results">No results available</li>
                  </ul>
                )}
              </div>
            </div>

            {!sendToAllUsers && dlEmails && (
              <div className="bc-dl-field">
                <label>DL (Distribution List Emails)</label>
                <textarea readOnly value={dlEmails} rows={3} />
                {memberCount !== null && (
                  <span style={{ fontSize: '12px', color: 'green' }}>{memberCount} members loaded</span>
                )}
              </div>
            )}

            <div className="bc-validated-emails">
              {validatedEmails.invalidEmails.map(email => (
                <span key={email}>{email}</span>
              ))}
            </div>

            {/* Subject + Type */}
            {/* Type */}
            <div className="bc-subject-row">
              <div className="bc-type-wrap">
                <label htmlFor="bc-type">Type</label>
                <select id="bc-type" value={type} onChange={handleTypeChange}>
                  <option value="msg">Message (Send Activity)</option>
                  <option value="table">Table (Adaptive Card)</option>
                </select>
              </div>
            </div>

            {/* Editor */}
            {type === 'table' ? (
              <div>
                <p style={{ fontSize: '13px', color: '#888780', marginBottom: '10px', marginTop: '16px' }}>
                  Fill in the fields you want to include (all fields are optional):
                </p>
                {templateContent.map((item, index) => (
                  <div key={index} className="bc-template-row">
                    <label>{item.title}</label>
                    <ReactQuill
                      className="bc-quill"
                      value={item.content}
                      onChange={(content) => handleTemplateChange(index, content)}
                      modules={tableModules}
                      formats={formats}
                      placeholder={`Enter ${item.title.toLowerCase()} content...`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <label>Message Content</label>
                <ReactQuill
                  className="bc-quill"
                  value={text}
                  onChange={setText}
                  modules={messageModules}
                  formats={formats}
                  placeholder="Enter your message content..."
                />
              </div>
            )}

            {/* Live Preview */}
            {type === 'msg' && (
              <div className="bc-preview-section">
                <span className="bc-section-label">Message Preview</span>
                <div className="bc-preview-container">
                  {isQuillEmpty(text) && attachments.length === 0 ? (
                    <div className="bc-preview-empty">
                      <span style={{ fontSize: '1.8rem', opacity: 0.3 }}>👁️</span>
                      <p>Start typing to see a preview…</p>
                    </div>
                  ) : (
                    <div className="bc-preview-cards">
                      {attachments.length > 0 && (
                        <div className="bc-preview-img-card">
                          <img src={attachments[0]} alt="preview" />
                        </div>
                      )}
                      {!isQuillEmpty(text) && (
                        <div className="bc-preview-text-card">
                          <div className="bc-preview-body" dangerouslySetInnerHTML={{ __html: text }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error.text && <p className="bc-error-message">{error.message}</p>}

            {/* Saved content debug */}
            {savedContent.map((content, index) => (
              <div key={index} className="bc-saved-message">
                <strong>Message Sent (Teams Format):</strong>
                <pre>{JSON.stringify(content, null, 2)}</pre>
              </div>
            ))}
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="bc-sidebar">

            {/* Box 1: Image Upload */}
            <div className="bc-sidebar-box">
              <p className="bc-sidebar-box-title">📎 Image</p>
              <div
                className={`bc-dropzone${isDragOver ? ' bc-dropzone-active' : ''}`}
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
              >
                {attachments.length === 0 ? (
                  <div className="bc-dropzone-content">
                    <span className="bc-upload-icon">📎</span>
                    <p className="bc-dropzone-hint">Drag & drop or</p>
                    <input
                      type="file" id="bc-attachment" key={attachmentKey}
                      onChange={handleAttachmentChange} accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="bc-attachment" className="bc-upload-btn">Browse file</label>
                  </div>
                ) : (
                  <div className="bc-attachment-preview">
                    <img src={attachments[0]} alt="attachment" className="bc-attachment-img" />
                    <button type="button" onClick={handleDeleteAttachment} className="bc-delete-btn" title="Remove image">✕</button>
                  </div>
                )}
              </div>
            </div>

            {/* Box 2: Advanced Options */}
            <div className="bc-sidebar-box">
              <p className="bc-sidebar-box-title">⚙️ Options</p>
              <div className="bc-advanced-content">
                {/* Send to all users */}
                <label className="bc-checkbox-label">
                  <input
                    type="checkbox" className="bc-checkbox"
                    checked={sendToAllUsers}
                    onChange={e => handleAllUsersToggle(e.target.checked)}
                  />
                  👥 Send to all users
                </label>
                {sendToAllUsers && (
                  <p className="bc-drafts-empty">Send To and AD Group are locked while this is on.</p>
                )}
                {/* Scheduled Messages */}
                <label className="bc-checkbox-label">
                  <input
                    type="checkbox" checked={showScheduledMsgs}
                    onChange={e => { setShowScheduledMsgs(e.target.checked); if (e.target.checked) fetchScheduledNotifications(); }}
                    className="bc-checkbox"
                  />
                  🕐 Scheduled Messages
                </label>

                {showScheduledMsgs && (
                  <div className="bc-drafts-list">
                    {isFetchingScheduled ? (
                      <p className="bc-drafts-empty">Loading...</p>
                    ) : scheduledNotifications.filter(n => n.status === 'pending').length === 0 ? (
                      <p className="bc-drafts-empty">No pending scheduled messages.</p>
                    ) : (
                      scheduledNotifications.filter(n => n.status === 'pending').map(n => (
                        <div key={n._id} className="bc-draft-card">
                          <span className="bc-draft-name">{n.notificationName || '(unnamed)'}</span>
                          <span className="bc-draft-date">Sends: {new Date(n.scheduledDateTime).toLocaleString()}</span>
                          <div className="bc-draft-actions">
                            <span className={`bc-sched-status bc-sched-${n.status}`}>{n.status}</span>
                            <button type="button" onClick={() => deleteScheduledNotification(n._id)} className="bc-draft-btn bc-draft-delete">Cancel</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Schedule for later */}
                <label className="bc-checkbox-label">
                  <input
                    type="checkbox" checked={isScheduled}
                    onChange={e => { setIsScheduled(e.target.checked); if (e.target.checked) { setShowSavedDrafts(false); setScheduleInputsVisible(true); } }}
                    className="bc-checkbox"
                  />
                  📅 Schedule for later
                </label>

                {isScheduled && scheduleInputsVisible && (
                  <div className="bc-sch-wrap">
                    <p className="bc-sch-label">Pick a date</p>
                    <div className="bc-date-strip">
                      {getNext5Days().map(day => (
                        <button
                          key={day.isoDate} type="button"
                          className={`bc-day-tile${scheduleDate === day.isoDate ? ' active' : ''}`}
                          onClick={() => setScheduleDate(day.isoDate)}
                        >
                          <span className="bc-day-name">{day.label}</span>
                          <span className="bc-day-num">{day.num}</span>
                        </button>
                      ))}
                    </div>

                    <p className="bc-sch-label" style={{ marginTop: '10px' }}>Pick a time</p>
                    <div className="bc-time-slots">
                      {QUICK_TIMES.map(t => (
                        <button
                          key={t} type="button"
                          className={`bc-time-pill${selectedQuickTime === t ? ' active' : ''}`}
                          onClick={() => { setSelectedQuickTime(t); setScheduleTime(to24h(t)); if (scheduleDate) setTimeout(() => setScheduleInputsVisible(false), 400); }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>

                    <div className="bc-custom-time-row">
                      <input
                        type="time" className="bc-custom-time-input"
                        value={selectedQuickTime ? '' : scheduleTime}
                        onChange={e => { setSelectedQuickTime(null); setScheduleTime(e.target.value); if (scheduleDate && e.target.value) setTimeout(() => setScheduleInputsVisible(false), 600); }}
                      />
                    </div>

                    {scheduleDate && scheduleTime && (
                      <div className="bc-schedule-summary">
                        ✅ Sends on <strong>{getNext5Days().find(d => d.isoDate === scheduleDate)?.display || scheduleDate}</strong> at <strong>{toDisplay(scheduleTime)}</strong>
                      </div>
                    )}
                  </div>
                )}

                {isScheduled && !scheduleInputsVisible && scheduleDate && scheduleTime && (
                  <div
                    className="bc-schedule-summary"
                    style={{ cursor: 'pointer', marginTop: '8px' }}
                    onClick={() => setScheduleInputsVisible(true)}
                    title="Click to change"
                  >
                    📅 <strong>{new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}</strong>
                    <span style={{ marginLeft: 'auto', fontSize: '10px', opacity: 0.6 }}>Edit</span>
                  </div>
                )}

                {/* Saved Drafts */}
                <label className="bc-checkbox-label">
                  <input
                    type="checkbox" checked={showSavedDrafts}
                    onChange={e => { setShowSavedDrafts(e.target.checked); if (e.target.checked) { setIsScheduled(false); fetchDraftTemplates(); } }}
                    className="bc-checkbox"
                  />
                  💾 Saved Drafts
                </label>

                {showSavedDrafts && (
                  <div className="bc-drafts-list">
                    {isFetchingDrafts ? (
                      <p className="bc-drafts-empty">Loading drafts...</p>
                    ) : draftTemplates.length === 0 ? (
                      <p className="bc-drafts-empty">No saved drafts yet.</p>
                    ) : (
                      draftTemplates.map(draft => (
                        <div key={draft._id} className="bc-draft-card">
                          <span className="bc-draft-name">{draft.templateName}</span>
                          <span className="bc-draft-date">{new Date(draft.createdAt).toLocaleString()}</span>
                          <div className="bc-draft-actions">
                            <button type="button" onClick={() => loadDraftTemplate(draft)} className="bc-draft-btn bc-draft-load">Load</button>
                            <button type="button" onClick={() => deleteDraftTemplate(draft._id)} className="bc-draft-btn bc-draft-delete">Delete</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Box 3: Actions */}
            <div className="bc-sidebar-box">
              <p className="bc-sidebar-box-title">✉️ Send</p>
              <div className="bc-actions-box">
                <button type="button" className="bc-btn-save" onClick={saveMessage}>💾 Save Draft</button>
                <button type="submit" className="bc-btn-publish">
                  {isScheduled ? '📅 Schedule & Send' : '🚀 Publish'}
                </button>
                <button type="button" onClick={clearMessage} className="bc-btn-clear">✕ Clear</button>
              </div>
            </div>

          </div>{/* end sidebar */}

        </form>
      </div>

      {/* Save Draft Modal — rendered outside bc-root intentionally (fixed overlay) */}
      {showTemplateNameModal && (
        <div className="bc-modal-overlay" onClick={() => {
          setShowTemplateNameModal(false);
          setIsScheduleSubmit(false);
          setPendingSchedulePayload(null);
        }}>
          {/* onClick={() => setShowTemplateNameModal(false)}> */}
          <div className="bc-modal" onClick={e => e.stopPropagation()}>
            <div className="bc-modal-header">
              <h3>{isScheduleSubmit ? '📅 Name this Schedule' : '💾 Save Draft'}</h3>
              <button type="button" onClick={() => {
                setShowTemplateNameModal(false);
                setIsScheduleSubmit(false);
                setPendingSchedulePayload(null);
              }} className="bc-modal-close">✕</button>
            </div>
            <div className="bc-modal-body">
              <span className="bc-section-label">Template Name *</span>
              <input
                type="text" value={templateNameInput}
                onChange={e => setTemplateNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && confirmSaveDraft()}
                placeholder={isScheduleSubmit ? "e.g. Finland Outage Alert, Q3 Sync…" : "e.g. Q3 Announcement, Finland Update…"}
                autoFocus
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e2e0da', fontSize: '14px', marginTop: '6px', fontFamily: 'inherit' }}
              />
            </div>
            <div className="bc-modal-footer">
              <button type="button" onClick={() => {
                setShowTemplateNameModal(false);
                setIsScheduleSubmit(false);
                setPendingSchedulePayload(null);
              }} className="bc-modal-cancel">Cancel</button>
              {/* <button type="button" onClick={() => setShowTemplateNameModal(false)} className="bc-modal-cancel">Cancel</button> */}
              <button type="button" onClick={confirmSaveDraft} className="bc-modal-save" disabled={isSavingDraft}>
                {isSavingDraft ? 'Saving…' : isScheduleSubmit ? 'Schedule' : 'Save Draft'}
                {/* {isSavingDraft ? 'Saving…' : 'Save Draft'} */}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ height: "150px" }}></div>
    </div>
  );
}

function handleFileUpload() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.onchange = async () => {
    const file = input.files[0];
    if (file) {
      try {
        const url = await uploadFile(file);
        const quill = this.quill;
        const range = quill.getSelection();
        quill.insertEmbed(range.index, 'image', url);
      } catch (error) { console.error('Error uploading file:', error); }
    }
  };
  input.click();
}

async function uploadFile(file) {
  return 'https://example.com/path/to/uploaded/file.jpg';
}